/**
 * V2PaywallFooterSection — V2-native editor section for paywall footer
 *
 * Edits: Purchase button, Auto-renew label, Restore, Terms, Privacy, Footer styles
 * V2 paths:
 * - content.paywall.purchaseAction (purchase button)
 * - content.paywall.autoRenewLabel (auto-renew)
 * - content.paywall.legalLinks.restore (restore)
 * - content.paywall.legalLinks.termsOfUse (terms)
 * - content.paywall.legalLinks.privacyPolicy (privacy)
 * - content.paywall.footerAppearance (footer styles)
 *
 * Replaces: OnboardingPaywallFooterSection (for V2 context)
 * Reuses: TextElement, StyleFieldRow
 *
 * Key V2 differences:
 * - l10n → translations
 * - styles → appearance
 * - footer.purchase → content.paywall.purchaseAction
 * - footer.bottomContainer.* → content.paywall.legalLinks.*
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { SAccordion, StyledTextField } from '@modules/ui-components';
import { TextElement } from '../../../factories/elements/TextElement';
import { V2TemplateTextElement } from '../../shared/templateFields/V2TemplateTextElement';
import { EditorGuideProvider } from '../../../contexts';
import { buildFlowVariableItems } from '../../../config/flowVariablesConfig';
import { variableResolver } from '@modules/v2-variable-utils';
import {
  V2_PAYWALL_PURCHASE_BUTTON_CONFIG,
  V2_PAYWALL_AUTO_RENEW_LABEL_CONFIG,
  V2_PAYWALL_RESTORE_CONFIG,
  V2_PAYWALL_TERMS_OF_USE_CONFIG,
  V2_PAYWALL_PRIVACY_POLICY_CONFIG,
  V2_PAYWALL_FOOTER_STYLES_CONFIG,
  V2_PAYWALL_FOOTER_BUTTON_CONFIGS,
  createDefaultV2PurchaseAction,
  createDefaultV2AutoRenewLabel,
  createDefaultV2LegalLinks,
} from '../../../config/v2PaywallFooterConfig';
import { withScreenEditorFieldGuides } from '../../../config/screenEditorFieldGuideResolver';
import { SECTION_MARGIN_BOTTOM, FIELD_GAP_SMALL } from '../../../config/ui';
import {
  expandedToggleButtonSx,
  expandedToggleButtonActiveSx,
  expandedSectionSx,
} from '../../../config/editorFieldStyles';
import { getValueByPath, isFieldEnabled, getV2TranslationValue } from '../../../utils/v2FieldOperations';
import {
  getStyleValues as getStyleValuesUtil,
  getButtonStyleValues as getButtonStyleValuesUtil,
} from '../../../utils/fieldValueOperations';
import { StyleFieldRow } from '../../shared/styleFields';
import {
  getSectionTestId,
  getFieldTestId,
  getCheckboxTestId,
  getStylesToggleTestId,
  getInputTestId,
  getStyleGroupTestId,
} from '../../../utils/testIdUtils';
import type { FieldConfig } from '../../../types/editorFieldTypes';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { FlowDocument } from '@segmently/schema-models/v2';

export interface V2PaywallFooterSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  /** Project ID for icon upload (V2 only) */
  projectId?: string;
  /** Onboarding ID for icon upload (V2 only) */
  onboardingId?: string;
  /** Version ID for icon upload (V2 only) */
  versionId?: string;
  /** Full FlowDocument for BFS-based variable resolution in @variable autocomplete */
  flowDocument?: FlowDocument | null;
}

export const V2PaywallFooterSection: React.FC<V2PaywallFooterSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  onboardingId,
  versionId,
  flowDocument,
}) => {
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [footerStylesExpanded, setFooterStylesExpanded] = useState(false);

  // BFS-scoped flow variables for @variable autocomplete in footer labels.
  const flowVariableItems = useMemo(() => {
    if (!flowDocument || !step.id) return [];
    const boundVars = variableResolver.findAvailableVariables(flowDocument, step.id);
    const varsRecord: Record<string, import('@modules/v2-variable-utils').FunnelVariable> = {};
    for (const bv of boundVars) {
      varsRecord[bv.variable.id] = bv.variable;
    }
    return buildFlowVariableItems(varsRecord);
  }, [flowDocument, step.id]);

  const toggleFieldExpansion = useCallback((path: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  // Get text value using V2 translations path
  const getFieldValue = useCallback(
    (config: FieldConfig): string => {
      if (config.l10nPath) {
        return getV2TranslationValue(step, config.l10nPath, locale);
      }
      return getValueByPath<string>(step, config.path) ?? '';
    },
    [step, locale]
  );

  const isEnabled = useCallback(
    (config: FieldConfig): boolean => isFieldEnabled(step, config.path),
    [step]
  );

  const getStyleValues = useCallback(
    (config: FieldConfig): Record<string, unknown> => {
      const result = getStyleValuesUtil(step as unknown as Record<string, unknown>, config);
      // V2 EdgeInsets uses top/bottom/start/end, translate to paddingTop/Right/Bottom/Left for PaddingElement
      if (config.paddingConfig) {
        const bp = config.paddingConfig.basePath;
        const insets = getValueByPath<Record<string, unknown>>(step, bp);
        if (insets && typeof insets === 'object') {
          result[`${bp}.paddingTop`] = insets.top ?? result[`${bp}.paddingTop`];
          result[`${bp}.paddingRight`] = insets.end ?? result[`${bp}.paddingRight`];
          result[`${bp}.paddingBottom`] = insets.bottom ?? result[`${bp}.paddingBottom`];
          result[`${bp}.paddingLeft`] = insets.start ?? result[`${bp}.paddingLeft`];
        }
      }
      return result;
    },
    [step]
  );

  const getButtonStyleValues = useCallback(
    (config: FieldConfig): Record<string, unknown> => {
      const result = getButtonStyleValuesUtil(step as unknown as Record<string, unknown>, config);
      // Transform onCancelAction Trigger → string for StyleFieldRow checkbox
      const downsalePath = 'content.paywall.purchaseAction.action.onCancelAction';
      if (downsalePath in result) {
        result[downsalePath] = result[downsalePath] != null ? 'enabled' : null;
      }
      return result;
    },
    [step]
  );

  // Write text value to V2 translations path
  const handleTextChange = useCallback(
    (config: FieldConfig, value: string) => {
      if (config.l10nPath) {
        onChange(`${config.l10nPath}.translations.${locale}`, value);
      }
    },
    [locale, onChange]
  );

  const handleEnabledChange = useCallback(
    (config: FieldConfig, enabled: boolean) => {
      if (enabled) {
        let defaultValue: unknown;
        if (config === V2_PAYWALL_PURCHASE_BUTTON_CONFIG) {
          defaultValue = createDefaultV2PurchaseAction(locale);
        } else if (config === V2_PAYWALL_AUTO_RENEW_LABEL_CONFIG) {
          defaultValue = createDefaultV2AutoRenewLabel(locale);
        } else if (config === V2_PAYWALL_RESTORE_CONFIG) {
          const legalLinks = createDefaultV2LegalLinks(locale);
          defaultValue = legalLinks.restore;
        } else if (config === V2_PAYWALL_TERMS_OF_USE_CONFIG) {
          const legalLinks = createDefaultV2LegalLinks(locale);
          defaultValue = legalLinks.termsOfUse;
        } else if (config === V2_PAYWALL_PRIVACY_POLICY_CONFIG) {
          const legalLinks = createDefaultV2LegalLinks(locale);
          defaultValue = legalLinks.privacyPolicy;
        } else {
          defaultValue = {
            translations: { [locale]: config.placeholder || '' },
            kind: 'static',
            templateParams: { labels: {}, links: {}, countdowns: {} },
            appearance: {},
            layout: { insets: {} },
          };
        }
        onChange(config.path, defaultValue);
      } else {
        onChange(config.path, null);
      }
    },
    [locale, onChange]
  );

  const handleStyleChange = useCallback(
    (path: string, value: unknown) => {
      // Downsale toggle: string → onCancelAction standard Trigger
      if (path.endsWith('.action.onCancelAction')) {
        onChange(path, value != null ? { kind: 'advance', edges: [] } : null);
        return;
      }
      // Translate V1 padding paths to V2 EdgeInsets
      const translatedPath = path
        .replace('.paddingTop', '.top')
        .replace('.paddingRight', '.end')
        .replace('.paddingBottom', '.bottom')
        .replace('.paddingLeft', '.start');
      onChange(translatedPath, value);
    },
    [onChange]
  );

  const getContainerStyleValue = useCallback(
    (path: string): unknown => getValueByPath(step, path),
    [step]
  );

  const renderUriField = (config: FieldConfig, enabled: boolean) => {
    if (!config.uriPath) return null;

    return (
      <Box sx={{ mt: -1, mb: 1, ml: 3.5 }} data-testid={`paywall-footer-uri-${config.path.split('.').pop()}`}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          URL
        </Typography>
        <StyledTextField
          value={getValueByPath<string>(step, config.uriPath) ?? ''}
          onChange={(val: string) => onChange(config.uriPath!, val)}
          placeholder="https://"
          disabled={disabled || !enabled}
          size="small"
          fullWidth
        />
      </Box>
    );
  };

  const renderTemplateField = (config: FieldConfig) => {
    const enabled = isEnabled(config);
    const styleValues = getStyleValues(config);
    const isExpanded = expandedFields.has(config.path);

    return (
      <Box key={config.path}>
        <V2TemplateTextElement
          step={step}
          fieldConfig={config}
          locale={locale}
          onChange={onChange}
          enabled={enabled}
          styleValues={styleValues}
          expandedStyles={isExpanded}
          onEnabledChange={(newEnabled) => handleEnabledChange(config, newEnabled)}
          onStylesExpandToggle={() => toggleFieldExpansion(config.path)}
          onStyleChange={handleStyleChange}
          disabled={disabled}
          screenType={step.screenType}
          flowVariableItems={flowVariableItems}
          data-testid={getFieldTestId('paywall-footer', config.label)}
          checkboxTestId={getCheckboxTestId('paywall-footer', config.label)}
          stylesToggleTestId={getStylesToggleTestId('paywall-footer', config.label)}
          inputTestId={getInputTestId('paywall-footer', config.label)}
          styleGroupTestId={getStyleGroupTestId('paywall-footer', config.label)}
        />
        {renderUriField(config, enabled)}
      </Box>
    );
  };

  // Render a text/button field
  const renderButtonField = (config: FieldConfig, enablePaywallVariables = false) => {
    const enabled = isEnabled(config);
    const value = getFieldValue(config);
    const styleValues = getStyleValues(config);
    const buttonStyleValues = getButtonStyleValues(config);
    const isExpanded = expandedFields.has(config.path);

    return (
      <TextElement
        key={config.path}
        fieldConfig={config}
        value={value}
        templateValue={config.defaultValue}
        enabled={enabled}
        locale={locale}
        onChange={(newValue) => handleTextChange(config, newValue)}
        onEnabledChange={(newEnabled) => handleEnabledChange(config, newEnabled)}
        disabled={disabled}
        projectId={projectId}
        onboardingId={onboardingId}
        versionId={versionId}
        expandedStyles={isExpanded}
        onStylesExpandToggle={() => toggleFieldExpansion(config.path)}
        styleValues={styleValues}
        buttonStyleValues={buttonStyleValues}
        onStyleChange={handleStyleChange}
        data-testid={getFieldTestId('paywall-footer', config.label)}
        checkboxTestId={getCheckboxTestId('paywall-footer', config.label)}
        stylesToggleTestId={getStylesToggleTestId('paywall-footer', config.label)}
        inputTestId={getInputTestId('paywall-footer', config.label)}
        styleGroupTestId={getStyleGroupTestId('paywall-footer', config.label)}
        enablePaywallVariables={enablePaywallVariables}
        uriValue={config.uriPath ? (getValueByPath<string>(step, config.uriPath) ?? '') : undefined}
        onUriChange={config.uriPath ? (val: string) => onChange(config.uriPath!, val) : undefined}
      />
    );
  };

  return (
    <EditorGuideProvider>
      <SAccordion
        title="Footer"
        defaultExpanded={defaultExpanded}
        data-testid={getSectionTestId('paywall-footer')}
        sx={{ mb: SECTION_MARGIN_BOTTOM }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: FIELD_GAP_SMALL }}>
          {/* Purchase Button (downsale toggle is inside buttonStyleFields) */}
          {renderButtonField(V2_PAYWALL_PURCHASE_BUTTON_CONFIG)}

          {/* Footer labels use the V2 template editor for links, labels, countdowns, flow variables, and pricing variables. */}
          {renderTemplateField(V2_PAYWALL_AUTO_RENEW_LABEL_CONFIG)}

          {/* Restore */}
          {renderTemplateField(V2_PAYWALL_RESTORE_CONFIG)}

          {/* Terms of Use */}
          {renderTemplateField(V2_PAYWALL_TERMS_OF_USE_CONFIG)}

          {/* Privacy Policy */}
          {renderTemplateField(V2_PAYWALL_PRIVACY_POLICY_CONFIG)}

          {/* Footer Container Styles */}
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: FIELD_GAP_SMALL }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight={500}>
                Container Styles
              </Typography>
              <IconButton
                data-testid="paywall-footer-container-toggle"
                size="small"
                onClick={() => setFooterStylesExpanded(!footerStylesExpanded)}
                disabled={disabled}
                sx={footerStylesExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Box>
            <Collapse in={footerStylesExpanded} timeout="auto" unmountOnExit>
              <Box sx={expandedSectionSx}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  {withScreenEditorFieldGuides(V2_PAYWALL_FOOTER_STYLES_CONFIG.styleFields ?? [], 'paywallFooter').map((styleConfig) => (
                    <StyleFieldRow
                      key={styleConfig.path}
                      config={styleConfig}
                      value={getContainerStyleValue(styleConfig.path)}
                      onChange={(newValue) => handleStyleChange(styleConfig.path, newValue)}
                      disabled={disabled}
                    />
                  ))}
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </SAccordion>
    </EditorGuideProvider>
  );
};

export default V2PaywallFooterSection;
